import { IPrAnnualTaxSettlementEntity, IPrPayslipEntity } from "@odata/GeneratedEntityTypes";
import { PrAnnualTaxItemTypeCode } from "@odata/GeneratedEnums";
import { getCompanyCurrency } from "@utils/CompanyUtils";

import CurrencyType from "../../../types/Currency";
import { FormStorage } from "../../../views/formView/FormStorage";

interface IFormListRowDef {
    id: PrAnnualTaxItemTypeCode | string;
    sub?: PrAnnualTaxItemTypeCode[];
    isSubRow?: boolean;
    isInteger?: boolean;
    unit?: string;
    title?: string;
    isBold?: boolean;
    number?: number;
    value?: number | null;
}

export enum AnnualTaxGroupType {
    TaxBase = "TaxBase",
    NonTaxable = "NonTaxable",
    Tax = "Tax",
    Benefit = "Benefit",
    Compensation = "Compensation"
}

export function getOverviewSectionsByType(storage: FormStorage<IPrPayslipEntity>, groupType: AnnualTaxGroupType): IFormListRowDef[] {
    const { Items } = storage.getEntity<IPrAnnualTaxSettlementEntity>();

    return getRowDefByGroupType(groupType).map(row => {
        const item = Items.find(i => i.TypeCode === row.id);
        let value = row.isInteger ? item?.IntValue : item?.DecimalValue;
        if (row.sub?.length) {
            row.sub.forEach(subId => {
                const subItem = Items.find(i => i.TypeCode === subId);
                const subValue = row.isInteger ? subItem?.IntValue : subItem?.DecimalValue;
                value = value - (subValue ?? 0);
            });
        }
        return {
            ...row,
            unit: row.unit ?? CurrencyType.getCurrencyUnit(getCompanyCurrency(storage.context)),
            title: storage.t(`AnnualTaxSettlement:Rows.${row.title ?? `Num${row.number}`}`),
            value
        };
    });
}

export function getRowDefByGroupType(groupType: AnnualTaxGroupType): IFormListRowDef[] {
    switch (groupType) {
        case AnnualTaxGroupType.TaxBase:
            return taxBaseRowsDef;
        case AnnualTaxGroupType.NonTaxable:
            return nonTaxableRowsDef;
        case AnnualTaxGroupType.Tax:
            return taxRowsDef;
        case AnnualTaxGroupType.Benefit:
            return benefitRowsDef;
        case AnnualTaxGroupType.Compensation:
            return compensationRowsDef;
    }
}

const taxBaseRowsDef: IFormListRowDef[] = [{
    id: PrAnnualTaxItemTypeCode.TaxableIncomeTotal,
    number: 1
}];

const nonTaxableRowsDef: IFormListRowDef[] = [{
    id: PrAnnualTaxItemTypeCode.ValueOfGifts,
    number: 2
}, {
    id: PrAnnualTaxItemTypeCode.InterestOnLoans,
    number: 3
}, {
    id: "Sub1",
    title: "Sub1",
    isBold: true,
}, {
    id: PrAnnualTaxItemTypeCode.SupplementaryPensionSavingsOld,
    number: 5,
    isSubRow: true,
}, {
    id: PrAnnualTaxItemTypeCode.SupplementaryPensionSavingsNew,
    number: 6,
    isSubRow: true,
}, {
    id: PrAnnualTaxItemTypeCode.PensionInsurance,
    number: 7,
    isSubRow: true,
}, {
    id: PrAnnualTaxItemTypeCode.LifeInsurance,
    number: 8,
    isSubRow: true,
}, {
    id: PrAnnualTaxItemTypeCode.DIP,
    number: 9,
    isSubRow: true,
}, {
    id: PrAnnualTaxItemTypeCode.LongtermCareInsurance,
    number: 10
}, {
    id: PrAnnualTaxItemTypeCode.NontaxableTotal,
    number: 11,
    isBold: true
}];

const taxRowsDef: IFormListRowDef[] = [{
    id: PrAnnualTaxItemTypeCode.LoweredTaxableIncome,
    number: 12,
    isBold: true
}, {
    id: PrAnnualTaxItemTypeCode.CalculatedTax,
    number: 13,
    isBold: true
}, {
    id: PrAnnualTaxItemTypeCode.DistrainmentDiscount,
    number: 14
}, {
    id: "Sub2",
    title: "Sub2",
    isBold: true,
}, {
    id: PrAnnualTaxItemTypeCode.TaxpayerDiscount,
    number: 15,
    isSubRow: true,
}, {
    id: PrAnnualTaxItemTypeCode.SpouseDiscount,
    number: 16,
    isSubRow: true,
}, {
    id: PrAnnualTaxItemTypeCode.DisabilityI_AndII_Degree,
    number: 17,
    isSubRow: true,
}, {
    id: PrAnnualTaxItemTypeCode.DisabilityIII_Degree,
    number: 18,
    isSubRow: true,
}, {
    id: PrAnnualTaxItemTypeCode.ZTP_PDiscount,
    number: 19,
    isSubRow: true,
}, {
    id: PrAnnualTaxItemTypeCode.DiscountTotal,
    number: 20,
    isBold: true
}, {
    id: PrAnnualTaxItemTypeCode.CalculatedTaxBeforeAdvantage,
    number: 21,
    isBold: true
}, {
    id: PrAnnualTaxItemTypeCode.PaidTax,
    number: 22,
    isBold: true
}, {
    id: PrAnnualTaxItemTypeCode.Overpayment_Underpayment,
    number: 23,
    isBold: true
}];

const benefitRowsDef: IFormListRowDef[] = [{
    id: "Sub3",
    title: "Sub3",
    isBold: true
}, {
    id: PrAnnualTaxItemTypeCode.AdvantageTotal,
    number: 24
}, {
    id: PrAnnualTaxItemTypeCode.TaxDiscount,
    number: 25
}, {
    id: PrAnnualTaxItemTypeCode.AdvantageTotal,
    sub: [PrAnnualTaxItemTypeCode.TaxDiscount],
    number: 26
}, {
    id: "Sub4",
    title: "Sub4",
    isBold: true
}, {
    id: PrAnnualTaxItemTypeCode.CalculatedTax,
    number: 27
}, {
    id: PrAnnualTaxItemTypeCode.PaidTax,
    sub: [PrAnnualTaxItemTypeCode.CalculatedTax],
    number: 28
}, {
    id: "Sub5",
    title: "Sub5",
    isBold: true
}, {
    id: PrAnnualTaxItemTypeCode.ReceivedBonuses,
    number: 29
}, {
    id: PrAnnualTaxItemTypeCode.AdvantageTotal,
    sub: [PrAnnualTaxItemTypeCode.TaxDiscount, PrAnnualTaxItemTypeCode.ReceivedBonuses],
    number: 30
}];

const compensationRowsDef: IFormListRowDef[] = [{
    id: PrAnnualTaxItemTypeCode.Overpayment_Underpayment,
    number: 31,
    isBold: true
}, {
    id: PrAnnualTaxItemTypeCode.PaidTax,
    sub: [PrAnnualTaxItemTypeCode.CalculatedTax],
    number: 32,
    isBold: true
}, {
    id: PrAnnualTaxItemTypeCode.BonusDifference,
    title: "Sub32b",
    isBold: true
}];